// Step 1 SVG canvas helper.
//
// We deliberately avoid Konva (or any drawing dependency) — the wizard ships
// as a tiny no-deps bundle so it can embed inside iframes without bloat. The
// canvas is a single <svg> rendered into the host element each time the
// room state changes. Coordinates are in mm so the data we ship to the
// backend matches reality 1:1; the SVG `viewBox` does the on-screen scaling.
//
// Pointer events handle mouse + touch + pen via a single code path.

import type { RoomDimensions, RoomMarker, Wall } from "../types";

/** Tool currently armed in the toolbar. */
export type CanvasTool = "wall" | "door" | "window" | "boiler" | "select";

/** Snap radius (in mm) — pointer within this distance to an endpoint snaps to it. */
const SNAP_MM = 250;
/** Snap radius for grid (in mm) — every 100mm. */
const GRID_MM = 100;

/** Default opening widths in mm — wide enough to be visible without being silly. */
export const DEFAULT_WIDTH_MM: Record<RoomMarker["kind"], number> = {
  door: 800,
  window: 1200,
  boiler: 600,
};

export interface CanvasCallbacks {
  /** Called when the canvas wants to commit an undoable change. */
  commit: (next: RoomDimensions) => void;
}

export interface CanvasHandle {
  /** Re-render after an external state change (e.g. undo/redo). */
  rerender: () => void;
  /** Switch the active tool. */
  setTool: (t: CanvasTool) => void;
  /** Toggle fullscreen on the wrapping element (browser fullscreen API). */
  toggleFullscreen: () => void;
  /** Get the current tool. */
  getTool: () => CanvasTool;
  /** Set the stage-style directional cursor shown at the end of the latest segment. */
  setCursor: (p: { x: number; y: number }, angleDeg: number) => void;
  /** Light up one wall — used while its length field has focus. */
  setHighlight: (wallId: string | null) => void;
}

interface CanvasContext {
  host: HTMLElement;
  wrap: HTMLElement;
  svg: SVGSVGElement;
  /** Current arrangement, owned by the caller. We read it each render. */
  getRoom: () => RoomDimensions;
  cbs: CanvasCallbacks;
  tool: CanvasTool;
  /** Wall in-progress when tool === "wall" (start point set, awaiting end). */
  pending: { x: number; y: number } | null;
  /** Current fitted viewBox values in mm. */
  viewBox: { x: number; y: number; width: number; height: number };
  /** Directional cursor arrow shown at the right/end point of the next segment. */
  cursorArrow: { x: number; y: number; angleDeg: number } | null;
  /** In-flight "move the whole plan" drag under the `select` tool. Holds the
   *  grab point and the wall positions as they were when the drag started, so
   *  each move is applied to the original geometry rather than compounding. */
  drag: { originX: number; originY: number; walls: Wall[] } | null;
  /** In-flight "slide an opening along its wall" drag. Takes precedence over
   *  the whole-plan drag when the pointer goes down on a door/window/boiler. */
  markerDrag: { id: string; kind: RoomMarker["kind"] } | null;
  /** Wall drawn in the brand colour — mirrors the focused length field. */
  highlightId: string | null;
}

/** Construct + return a handle. Renders into `host`. */
export function createCanvas(
  host: HTMLElement,
  getRoom: () => RoomDimensions,
  cbs: CanvasCallbacks,
): CanvasHandle {
  host.innerHTML = `
    <div class="wiz-canvas-wrap" data-role="wrap">
      <button class="wiz-canvas-fs" data-role="fs" title="Toggle fullscreen" aria-label="Toggle fullscreen">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/>
        </svg>
      </button>
      <svg class="wiz-canvas" data-role="svg" preserveAspectRatio="xMidYMid meet"></svg>
    </div>
  `;
  const wrap = host.querySelector("[data-role=wrap]") as HTMLElement;
  const svg = host.querySelector("[data-role=svg]") as SVGSVGElement;
  const fsBtn = host.querySelector("[data-role=fs]") as HTMLButtonElement;

  const ctx: CanvasContext = {
    host,
    wrap,
    svg,
    getRoom,
    cbs,
    tool: "wall",
    pending: null,
    viewBox: { x: -3000, y: -3000, width: 6000, height: 6000 },
    cursorArrow: { x: 0, y: 0, angleDeg: 0 },
    drag: null,
    markerDrag: null,
    highlightId: null,
  };

  // Pointer handling ─────────────────────────────────────────────────────────
  svg.addEventListener("pointerdown", (e) => onPointerDown(ctx, e));
  svg.addEventListener("pointermove", (e) => onPointerMove(ctx, e));
  const endDrag = (e: PointerEvent): void => {
    if (!ctx.drag && !ctx.markerDrag) return;
    ctx.drag = null;
    ctx.markerDrag = null;
    try { svg.releasePointerCapture(e.pointerId); } catch { /* already released */ }
  };
  svg.addEventListener("pointerup", endDrag);
  svg.addEventListener("pointercancel", endDrag);
  svg.addEventListener("pointerleave", () => {
    // A move drag survives the pointer leaving the SVG (we captured it); only
    // the wall-drawing ghost is cancelled here.
    if (ctx.drag || ctx.markerDrag) return;
    ctx.pending = null;
    render(ctx);
  });

  // Fullscreen toggle (uses the wrap, so the toolbar can sit beside it).
  fsBtn.addEventListener("click", (e) => {
    e.preventDefault();
    toggleFullscreen(ctx);
  });

  document.addEventListener("fullscreenchange", () => {
    const active = document.fullscreenElement === wrap;
    svg.classList.toggle("wiz-canvas--full", active);
  });

  render(ctx);

  return {
    rerender: () => render(ctx),
    setTool: (t) => {
      ctx.tool = t;
      ctx.pending = null;
      render(ctx);
    },
    toggleFullscreen: () => toggleFullscreen(ctx),
    getTool: () => ctx.tool,
    setCursor: (p, angleDeg) => {
      ctx.cursorArrow = { x: p.x, y: p.y, angleDeg };
      render(ctx);
    },
    setHighlight: (wallId) => {
      if (ctx.highlightId === wallId) return;
      ctx.highlightId = wallId;
      render(ctx);
    },
  };
}

// ── Rendering ────────────────────────────────────────────────────────────────

function render(ctx: CanvasContext): void {
  const room = ctx.getRoom();
  fitViewBox(ctx, room);
  ctx.svg.setAttribute("viewBox", `${ctx.viewBox.x} ${ctx.viewBox.y} ${ctx.viewBox.width} ${ctx.viewBox.height}`);

  const parts: string[] = [];
  const labels: string[] = [];

  // Cabinet runs first, so walls, openings and labels all draw over them.
  parts.push(renderCabinetRuns(room));

  // Grid is drawn via CSS background on .wiz-canvas, so we only render walls,
  // markers, endpoints, and the pending wall ghost. Measurement labels are
  // deliberately rendered last so doors/windows can never cover them.
  // Numbered only on template plans, where the step lists "Wall 1, Wall 2, …"
  // with a length field each — the badge is what ties the two together. A
  // hand-drawn plan has no such list, so it stays uncluttered.
  const numbered = isTemplatePlan(room.walls);
  room.walls.forEach((w, i) => {
    const hl = w.id === ctx.highlightId ? " wall--hl" : "";
    parts.push(`<line class="wall${hl}" x1="${w.x1}" y1="${w.y1}" x2="${w.x2}" y2="${w.y2}" />`);
    labels.push(dimensionLabel(w));
    if (numbered) labels.push(wallNumberBadge(w, i + 1, w.id === ctx.highlightId));
  });

  // Markers (door / window / boiler) anchored along their wall.
  for (const m of [...room.doors, ...room.windows, ...room.boilers]) {
    parts.push(renderMarker(m, room.walls));
  }

  // Endpoints.
  for (const p of endpointSet(room.walls)) {
    parts.push(`<circle class="endpoint" cx="${p.x}" cy="${p.y}" r="14" />`);
  }

  parts.push(...labels);

  if (ctx.cursorArrow) {
    parts.push(renderCursorArrow(ctx.cursorArrow));
  }

  // Pending wall ghost.
  if (ctx.pending && ctx.tool === "wall") {
    parts.push(
      `<line class="wall wall--ghost" x1="${ctx.pending.x}" y1="${ctx.pending.y}" ` +
        `x2="${ctx.pending.x}" y2="${ctx.pending.y}" data-role="ghost" />`,
    );
  }

  ctx.svg.innerHTML = parts.join("");
}

function renderCursorArrow(a: { x: number; y: number; angleDeg: number }): string {
  return `<g class="cursor-arrow" transform="translate(${Math.round(a.x)} ${Math.round(a.y)}) rotate(${a.angleDeg})">
    <path d="M 0 0 L -210 -115 L -150 0 L -210 115 Z" />
    <circle cx="0" cy="0" r="28" />
  </g>`;
}

function dimensionLabel(w: Wall): string {
  const lenMm = Math.round(Math.hypot(w.x2 - w.x1, w.y2 - w.y1));
  if (lenMm < 100) return "";
  const mx = Math.round((w.x1 + w.x2) / 2);
  const my = Math.round((w.y1 + w.y2) / 2);
  // Stage-style readout: raw millimetres, horizontally centred above the
  // element. Do not convert to metres and do not rotate to the segment angle.
  return `<text class="dim-label" x="${mx}" y="${my - 170}" text-anchor="middle" dominant-baseline="middle">${lenMm}</text>`;
}

// ── Cabinet runs ─────────────────────────────────────────────────────────────
// A kitchen "shape" is where the UNITS go — the room's footprint is a separate
// question — so we shade the walls the runs sit against rather than reshaping
// the room. Without this, two templates could draw the same outline and the
// tile you picked would have no visible effect.
//
// Derived purely from `layoutType` + the walls at render time: no stored
// geometry, nothing to keep in sync, and it follows the plan when it's dragged.
// Decorative and non-interactive — hit-testing for openings and the plan-move
// drag is unaffected.
//
// 2026-08-11: runs are worked out per WALL off its outward normal rather than
// off the bounding box.
//
// 2026-08-12: a template is now the cabinet run itself — one, two, two or three
// walls, never a closed room — so EVERY template wall carries a run and there
// is no per-layout side list to keep in step with the tiles any more.

/** Real-world worktop depth. Drawing it to scale means the bars read as
 *  furniture rather than as an abstract highlight. */
const RUN_DEPTH_MM = 600;

/** Which way a wall faces, away from the kitchen. */
type Side = "north" | "east" | "south" | "west";

function planBounds(walls: Wall[]): { minX: number; minY: number; maxX: number; maxY: number } | null {
  if (walls.length === 0) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const w of walls) {
    minX = Math.min(minX, w.x1, w.x2);
    minY = Math.min(minY, w.y1, w.y2);
    maxX = Math.max(maxX, w.x1, w.x2);
    maxY = Math.max(maxY, w.y1, w.y2);
  }
  return { minX, minY, maxX, maxY };
}

/** Was this plan generated by a shape template? `layoutType` survives switching
 *  to the freeform planner, and painting run bars along a hand-drawn wall would
 *  put units through a bay or across a corner that isn't there. */
function isTemplatePlan(walls: Wall[]): boolean {
  return walls.length > 0 && walls.every((w) => w.id.startsWith("tpl-"));
}

/** Which way a wall faces, given every template wall is drawn with the kitchen
 *  on its left (screen space, y downward): the outward normal of a wall drawn
 *  (dx, dy) is (dy, −dx). Returns null for anything not axis-aligned. */
function outwardSide(w: Wall): Side | null {
  const dx = w.x2 - w.x1;
  const dy = w.y2 - w.y1;
  if (dx !== 0 && dy !== 0) return null;
  if (dx === 0) return dy > 0 ? "east" : "west";
  return dx > 0 ? "north" : "south";
}

function renderCabinetRuns(room: RoomDimensions): string {
  // Template plans only. `layoutType` survives switching to the freeform
  // planner, and a hand-drawn wall is not a cabinet run.
  if (!isTemplatePlan(room.walls)) return "";
  const b = planBounds(room.walls);
  if (!b) return "";
  const spanX = b.maxX - b.minX;
  const spanY = b.maxY - b.minY;

  return room.walls
    .map((w) => {
      const side = outwardSide(w);
      if (!side) return "";
      // Never let facing runs meet in the middle: in a 1m galley two 600mm
      // bars would otherwise be units wall to wall. A shape with no extent
      // across the wall at all — a single wall — has nothing to crowd.
      const across = side === "north" || side === "south" ? spanY : spanX;
      const d = across > 0 ? Math.min(RUN_DEPTH_MM, across / 3) : RUN_DEPTH_MM;
      const x1 = Math.min(w.x1, w.x2);
      const x2 = Math.max(w.x1, w.x2);
      const y1 = Math.min(w.y1, w.y2);
      const y2 = Math.max(w.y1, w.y2);
      const r =
        side === "north" ? { x: x1,     y: y1,     w: x2 - x1, h: d } :
        side === "south" ? { x: x1,     y: y2 - d, w: x2 - x1, h: d } :
        side === "west"  ? { x: x1,     y: y1,     w: d,       h: y2 - y1 } :
                           { x: x2 - d, y: y1,     w: d,       h: y2 - y1 };
      if (r.w <= 0 || r.h <= 0) return "";
      return `<rect class="cabinet-run" x="${r.x}" y="${r.y}" width="${r.w}" height="${r.h}" />`;
    })
    .join("");
}

/** Small numbered badge tying a wall to its length field in the step. Sits on
 *  the room side of the wall, opposite the mm dimension label. */
function wallNumberBadge(w: Wall, n: number, active: boolean): string {
  const side = outwardSide(w);
  if (!side) return "";
  const off = 260;
  const x = Math.round((w.x1 + w.x2) / 2 + (side === "west" ? off : side === "east" ? -off : 0));
  const y = Math.round((w.y1 + w.y2) / 2 + (side === "north" ? off : side === "south" ? -off : 0));
  return (
    `<g class="wall-no${active ? " wall-no--hl" : ""}">` +
    `<circle cx="${x}" cy="${y}" r="150" />` +
    `<text x="${x}" y="${y}" text-anchor="middle" dominant-baseline="central">${n}</text>` +
    `</g>`
  );
}

function renderMarker(m: RoomMarker, walls: Wall[]): string {
  const wall = walls.find((w) => w.id === m.wallId);
  if (!wall) return "";
  const wallLen = Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1);
  if (wallLen < 1) return "";
  const dx = (wall.x2 - wall.x1) / wallLen;
  const dy = (wall.y2 - wall.y1) / wallLen;
  const cx = wall.x1 + dx * wallLen * m.t;
  const cy = wall.y1 + dy * wallLen * m.t;
  const half = m.widthMm / 2;
  if (m.kind === "boiler") {
    return `<circle class="boiler" cx="${cx}" cy="${cy}" r="80" data-marker-id="${m.id}" />`;
  }

  // Stage-style openings: draw doors/windows as clean line segments on the wall,
  // never as filled rectangles, so wall geometry stays readable.
  const x1 = Math.round(cx - dx * half);
  const y1 = Math.round(cy - dy * half);
  const x2 = Math.round(cx + dx * half);
  const y2 = Math.round(cy + dy * half);
  const cls = m.kind === "door" ? "door" : "window";
  return `<line class="${cls}" x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" data-marker-id="${m.id}" />`;
}

// ── Static floor-plan render (Bug 12) ───────────────────────────────────────
// Produces a self-contained SVG of the room (walls + door/window/boiler
// markers + dimension labels) with INLINE styles, so it renders identically
// in-page on the review step AND as a standalone image (saved as a shared
// design file on submit → CRM docs + customer portal). Stroke widths are in
// mm-space (the viewBox units) so they scale with the plan. Returns "" when
// no walls have been drawn.
export function renderRoomSvg(room: RoomDimensions): string {
  if (!room || !Array.isArray(room.walls) || room.walls.length === 0) return "";

  const pts: Array<{ x: number; y: number }> = [];
  for (const w of room.walls) {
    pts.push({ x: w.x1, y: w.y1 }, { x: w.x2, y: w.y2 });
    pts.push({ x: (w.x1 + w.x2) / 2, y: (w.y1 + w.y2) / 2 - 220 });
  }
  for (const m of [...room.doors, ...room.windows, ...room.boilers]) {
    const wall = room.walls.find((w) => w.id === m.wallId);
    if (!wall) continue;
    const len = Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1);
    if (len < 1) continue;
    pts.push({
      x: wall.x1 + ((wall.x2 - wall.x1) / len) * len * m.t,
      y: wall.y1 + ((wall.y2 - wall.y1) / len) * len * m.t,
    });
  }
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of pts) {
    minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
  }
  const rawW = Math.max(1, maxX - minX), rawH = Math.max(1, maxY - minY);
  const pad = Math.max(260, Math.max(rawW, rawH) * 0.12);
  const aspect = 1.6;
  let vbW = rawW + pad * 2, vbH = rawH + pad * 2;
  if (vbW / vbH > aspect) vbH = vbW / aspect; else vbW = vbH * aspect;
  vbW = Math.max(1200, Math.ceil(vbW)); vbH = Math.max(850, Math.ceil(vbH));
  const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
  const vbX = Math.round(cx - vbW / 2), vbY = Math.round(cy - vbH / 2);

  const parts: string[] = [];
  const labels: string[] = [];
  for (const w of room.walls) {
    parts.push(`<line class="wall" x1="${w.x1}" y1="${w.y1}" x2="${w.x2}" y2="${w.y2}" />`);
    labels.push(dimensionLabel(w));
  }
  for (const m of [...room.doors, ...room.windows, ...room.boilers]) parts.push(renderMarker(m, room.walls));
  for (const p of endpointSet(room.walls)) parts.push(`<circle class="endpoint" cx="${p.x}" cy="${p.y}" r="40" />`);
  parts.push(...labels);

  const style =
    `.bg{fill:#fbfbfd}.wall{stroke:#374151;stroke-width:60;fill:none;stroke-linecap:round}` +
    `.door{stroke:#8B5E3C;stroke-width:75;fill:none;stroke-linecap:round}` +
    `.window{stroke:#4A9BD9;stroke-width:75;fill:none;stroke-linecap:round}` +
    `.boiler{fill:#10B981;stroke:#fff;stroke-width:14}` +
    `.endpoint{fill:#e58200;stroke:#fff;stroke-width:10}` +
    `.dim-label{font:700 220px sans-serif;fill:#475569;paint-order:stroke;stroke:#fff;stroke-width:36px;stroke-linejoin:round}`;

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vbX} ${vbY} ${vbW} ${vbH}" ` +
    `preserveAspectRatio="xMidYMid meet" role="img" aria-label="Your room floor plan">` +
    `<style>${style}</style>` +
    `<rect class="bg" x="${vbX}" y="${vbY}" width="${vbW}" height="${vbH}" />` +
    parts.join("") +
    `</svg>`
  );
}

function endpointSet(walls: Wall[]): Array<{ x: number; y: number }> {
  const seen = new Set<string>();
  const out: Array<{ x: number; y: number }> = [];
  for (const w of walls) {
    const a = `${Math.round(w.x1)}_${Math.round(w.y1)}`;
    const b = `${Math.round(w.x2)}_${Math.round(w.y2)}`;
    if (!seen.has(a)) { seen.add(a); out.push({ x: w.x1, y: w.y1 }); }
    if (!seen.has(b)) { seen.add(b); out.push({ x: w.x2, y: w.y2 }); }
  }
  return out;
}

function fitViewBox(ctx: CanvasContext, room: RoomDimensions): void {
  const points: Array<{ x: number; y: number }> = [];
  for (const w of room.walls) {
    points.push({ x: w.x1, y: w.y1 }, { x: w.x2, y: w.y2 });
    // Include label space above each segment so labels are never clipped.
    points.push({ x: (w.x1 + w.x2) / 2, y: (w.y1 + w.y2) / 2 - 220 });
  }
  for (const m of [...room.doors, ...room.windows, ...room.boilers]) {
    const wall = room.walls.find((w) => w.id === m.wallId);
    if (!wall) continue;
    const len = Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1);
    if (len < 1) continue;
    const dx = (wall.x2 - wall.x1) / len;
    const dy = (wall.y2 - wall.y1) / len;
    const cx = wall.x1 + dx * len * m.t;
    const cy = wall.y1 + dy * len * m.t;
    points.push({ x: cx, y: cy });
  }
  if (ctx.cursorArrow) {
    const a = ctx.cursorArrow;
    // Cursor art extends behind the endpoint; include it without letting it
    // dominate the auto-fit scale.
    points.push(
      { x: a.x - 190, y: a.y - 190 },
      { x: a.x + 190, y: a.y + 190 },
    );
  }

  if (points.length === 0) {
    ctx.viewBox = { x: -3000, y: -3000, width: 6000, height: 6000 };
    return;
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }

  const rawWidth = Math.max(1, maxX - minX);
  const rawHeight = Math.max(1, maxY - minY);
  const maxDim = Math.max(rawWidth, rawHeight);
  // Use enough padding for stroke, endpoint/cursor art and labels, while still
  // keeping the plan large and central in the canvas.
  const padding = Math.max(260, maxDim * 0.12);
  const contentWidth = rawWidth + padding * 2;
  const contentHeight = rawHeight + padding * 2;
  const rect = ctx.svg.getBoundingClientRect();
  const aspect = rect.width > 0 && rect.height > 0 ? rect.width / rect.height : 1.6;

  let vbWidth: number;
  let vbHeight: number;
  if (contentWidth / contentHeight > aspect) {
    vbWidth = contentWidth;
    vbHeight = contentWidth / aspect;
  } else {
    vbHeight = contentHeight;
    vbWidth = contentHeight * aspect;
  }

  // Snap to whole mm for stable rendering and keep a sensible minimum viewbox so
  // a single short wall is visible but not comically oversized.
  vbWidth = Math.max(1200, Math.ceil(vbWidth));
  vbHeight = Math.max(850, Math.ceil(vbHeight));
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  ctx.viewBox = {
    x: Math.round(cx - vbWidth / 2),
    y: Math.round(cy - vbHeight / 2),
    width: vbWidth,
    height: vbHeight,
  };
}

// ── Pointer handling ─────────────────────────────────────────────────────────

function svgPoint(svg: SVGSVGElement, e: PointerEvent): { x: number; y: number } {
  const rect = svg.getBoundingClientRect();
  const vb = svg.viewBox.baseVal;
  const x = ((e.clientX - rect.left) / rect.width) * vb.width + vb.x;
  const y = ((e.clientY - rect.top) / rect.height) * vb.height + vb.y;
  return { x, y };
}

function snapToGrid(v: number): number {
  return Math.round(v / GRID_MM) * GRID_MM;
}

function snapToEndpoint(p: { x: number; y: number }, walls: Wall[]): { x: number; y: number } {
  for (const w of walls) {
    for (const e of [{ x: w.x1, y: w.y1 }, { x: w.x2, y: w.y2 }]) {
      if (Math.hypot(p.x - e.x, p.y - e.y) < SNAP_MM) return e;
    }
  }
  return { x: snapToGrid(p.x), y: snapToGrid(p.y) };
}

function onPointerDown(ctx: CanvasContext, e: PointerEvent): void {
  e.preventDefault();
  const room = ctx.getRoom();
  const raw = svgPoint(ctx.svg, e);
  const p = snapToEndpoint(raw, room.walls);

  // `select` drags the whole plan. Placed room templates are ordinary walls,
  // so this is what makes them repositionable rather than locked in place.
  // Markers are stored as {wallId, t} — wall-relative — so doors and windows
  // travel with the walls for free.
  // Grab an existing opening and it drags, whatever tool is selected — no
  // mode-switch first. Checked BEFORE the tool branches so a door is always
  // directly draggable; with the door/window tool active this means landing on
  // an existing marker repositions it instead of stacking a second one on top,
  // which is what you'd want anyway.
  //
  // `wall` is the deliberate exception: mid-draw, a click near a door is the
  // customer placing a wall corner, not reaching for the door.
  if (ctx.tool !== "wall") {
    const hitMarker = markerAtPoint(raw, room);
    if (hitMarker) {
      ctx.markerDrag = { id: hitMarker.id, kind: hitMarker.kind };
      try { ctx.svg.setPointerCapture(e.pointerId); } catch { /* not fatal */ }
      return;
    }
  }

  if (ctx.tool === "select") {
    if (room.walls.length === 0) return;
    // Nothing grabbed — move the whole plan.
    ctx.drag = { originX: raw.x, originY: raw.y, walls: room.walls.map((w) => ({ ...w })) };
    try { ctx.svg.setPointerCapture(e.pointerId); } catch { /* not fatal */ }
    return;
  }

  if (ctx.tool === "wall") {
    if (!ctx.pending) {
      ctx.pending = p;
    } else {
      // Reject zero-length walls.
      if (Math.hypot(p.x - ctx.pending.x, p.y - ctx.pending.y) >= 200) {
        const wall: Wall = {
          id: makeId(),
          x1: ctx.pending.x,
          y1: ctx.pending.y,
          x2: p.x,
          y2: p.y,
        };
        ctx.cbs.commit({ ...room, walls: [...room.walls, wall] });
      }
      ctx.pending = null;
    }
    render(ctx);
    return;
  }

  if (ctx.tool === "door" || ctx.tool === "window" || ctx.tool === "boiler") {
    const hit = nearestWallHit(raw, room.walls);
    if (!hit) return;
    const m: RoomMarker = {
      id: makeId(),
      kind: ctx.tool,
      wallId: hit.wall.id,
      t: hit.t,
      widthMm: DEFAULT_WIDTH_MM[ctx.tool],
    };
    const next = { ...room };
    if (ctx.tool === "door")    next.doors    = [...room.doors,    m];
    if (ctx.tool === "window")  next.windows  = [...room.windows,  m];
    if (ctx.tool === "boiler")  next.boilers  = [...room.boilers,  m];
    ctx.cbs.commit(next);
    render(ctx);
    return;
  }
}

/** All openings in one list, so hit-testing doesn't care which bucket they're in. */
function allMarkers(room: RoomDimensions): RoomMarker[] {
  return [...room.doors, ...room.windows, ...room.boilers];
}

/** Point on a wall at position t (0..1). */
function pointAlongWall(w: Wall, t: number): { x: number; y: number } {
  return { x: w.x1 + (w.x2 - w.x1) * t, y: w.y1 + (w.y2 - w.y1) * t };
}

/** The opening under `p`, if any. Generous radius — these are finger targets
 *  on mobile, and being slightly too easy to grab beats being impossible. */
function markerAtPoint(p: { x: number; y: number }, room: RoomDimensions): RoomMarker | null {
  const byId = new Map(room.walls.map((w) => [w.id, w]));
  let best: RoomMarker | null = null;
  let bestDist = Infinity;
  for (const m of allMarkers(room)) {
    const w = byId.get(m.wallId);
    if (!w) continue;
    const c = pointAlongWall(w, m.t);
    const d = Math.hypot(p.x - c.x, p.y - c.y);
    // Half the opening's own width, with a floor so a narrow one stays grabbable.
    const radius = Math.max(m.widthMm / 2, 350);
    if (d <= radius && d < bestDist) { best = m; bestDist = d; }
  }
  return best;
}

/** Project a point onto a wall and return its 0..1 position, clamped so an
 *  opening can never slide off the end of the wall it belongs to. */
function tAlongWall(w: Wall, p: { x: number; y: number }): number {
  const dx = w.x2 - w.x1;
  const dy = w.y2 - w.y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return 0;
  const t = ((p.x - w.x1) * dx + (p.y - w.y1) * dy) / lenSq;
  return Math.max(0, Math.min(1, t));
}

function onPointerMove(ctx: CanvasContext, e: PointerEvent): void {
  if (ctx.markerDrag) {
    const room = ctx.getRoom();
    const wall = room.walls.find((w) =>
      allMarkers(room).some((m) => m.id === ctx.markerDrag!.id && m.wallId === w.id));
    if (!wall) return;
    const t = tAlongWall(wall, svgPoint(ctx.svg, e));
    const move = (list: RoomMarker[]): RoomMarker[] =>
      list.map((m) => (m.id === ctx.markerDrag!.id ? { ...m, t } : m));
    ctx.cbs.commit({
      ...room,
      doors: move(room.doors),
      windows: move(room.windows),
      boilers: move(room.boilers),
    });
    render(ctx);
    return;
  }
  if (ctx.drag) {
    const raw = svgPoint(ctx.svg, e);
    const dx = snapToGrid(raw.x - ctx.drag.originX);
    const dy = snapToGrid(raw.y - ctx.drag.originY);
    if (dx === 0 && dy === 0) return;
    const room = ctx.getRoom();
    ctx.cbs.commit({
      ...room,
      walls: ctx.drag.walls.map((w) => ({
        ...w,
        x1: w.x1 + dx, y1: w.y1 + dy,
        x2: w.x2 + dx, y2: w.y2 + dy,
      })),
    });
    render(ctx);
    return;
  }
  if (ctx.tool !== "wall" || !ctx.pending) return;
  const raw = svgPoint(ctx.svg, e);
  const room = ctx.getRoom();
  const p = snapToEndpoint(raw, room.walls);
  // Update the ghost line in place (no re-render of the whole tree).
  const ghost = ctx.svg.querySelector<SVGLineElement>("[data-role=ghost]");
  if (ghost) {
    ghost.setAttribute("x2", String(p.x));
    ghost.setAttribute("y2", String(p.y));
  }
}

/** Project a point onto each wall; return the closest hit within 800mm. */
function nearestWallHit(
  p: { x: number; y: number },
  walls: Wall[],
): { wall: Wall; t: number; dist: number } | null {
  let best: { wall: Wall; t: number; dist: number } | null = null;
  for (const w of walls) {
    const dx = w.x2 - w.x1;
    const dy = w.y2 - w.y1;
    const len2 = dx * dx + dy * dy;
    if (len2 < 1) continue;
    let t = ((p.x - w.x1) * dx + (p.y - w.y1) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const px = w.x1 + dx * t;
    const py = w.y1 + dy * t;
    const d = Math.hypot(p.x - px, p.y - py);
    if (d < 800 && (!best || d < best.dist)) {
      best = { wall: w, t, dist: d };
    }
  }
  return best;
}

function toggleFullscreen(ctx: CanvasContext): void {
  if (document.fullscreenElement === ctx.wrap) {
    try { void document.exitFullscreen(); } catch { /* ignore */ }
  } else {
    try { void ctx.wrap.requestFullscreen(); } catch { /* ignore */ }
  }
}

// ── ID generation ────────────────────────────────────────────────────────────

function makeId(): string {
  // Tiny non-secure id — collisions vanishingly unlikely within one session.
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}
